import React, {FC, useState, useEffect} from "react";
import useTranslate from "../../../core/languages/hooks/useTranslate";
import useCurrentLanguage from "../../../core/languages/hooks/useCurrentLanguage";
import NcModal from "../../../ui/shared/NcModal/NcModal";
import ButtonSecondary from "../../../ui/shared/Button/ButtonSecondary";
import ButtonPrimary from "../../../ui/shared/Button/ButtonPrimary";
import {SingleDatePicker} from "react-dates";
import moment, {Moment} from "moment";
import useNcId from "../../../ui/hooks/useNcId";
import "react-dates/initialize";
import "react-dates/lib/css/_datepicker.css";
import EventCity from "../../../eventCities/types/EventCity";

interface HotelPackageRoomDateSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (startDate: string, endDate: string) => void;
  eventCity: EventCity;
  minStartDate: string;
  maxEndDate: string;
  isPackageDates?: boolean;
}

const HotelWizardRoomDateSelectorModal: FC<HotelPackageRoomDateSelectorModalProps> = ({
                                                                                        isOpen,
                                                                                        onClose,
                                                                                        onConfirm,
                                                                                        minStartDate,
                                                                                        maxEndDate,
                                                                                        eventCity,
                                                                                        isPackageDates = false,
                                                                                      }) => {
  const t = useTranslate();
  const languageCode = useCurrentLanguage();
  const [startDate, setStartDate] = useState<Moment | null>(moment(minStartDate));
  const [endDate, setEndDate] = useState<Moment | null>(moment(maxEndDate));
  const [error, setError] = useState<string | undefined>(undefined);
  const [focusedInputStart, setFocusedInputStart] = useState(false);
  const [focusedInputEnd, setFocusedInputEnd] = useState(false);
  const startDateId = useNcId();
  const endDateId = useNcId();

  const minStartMoment = moment(minStartDate);
  const cityEventStartMoment = moment(eventCity.startDate);
  const minEndMoment = moment(maxEndDate);
  const cityEventEndMoment = moment(eventCity.endDate);

  // Calculate actual min and max for startDate (in case they're inverted)
  const actualMinStartDate = minStartMoment.isBefore(cityEventStartMoment) ? minStartMoment : cityEventStartMoment;
  const actualMaxStartDate = minStartMoment.isSameOrAfter(cityEventStartMoment) ? minStartMoment : cityEventStartMoment;

  // Calculate actual min and max for endDate (in case they're inverted)
  const actualMinEndDate = minEndMoment.isBefore(cityEventEndMoment) ? minEndMoment : cityEventEndMoment;
  const actualMaxEndDate = minEndMoment.isSameOrAfter(cityEventEndMoment) ? minEndMoment : cityEventEndMoment;

  // Reset dates when modal opens
  useEffect(() => {
    if (isOpen) {
      setStartDate(moment(minStartDate));
      setEndDate(moment(maxEndDate));
      setError(undefined);
    }
  }, [isOpen, minStartDate, maxEndDate]);

  const handleConfirm = () => {
    // Validate dates
    if (!startDate || !endDate) {
      setError(t("packages.validation.datesRequired"));
      return;
    }

    if (isPackageDates) {
      // Check if start date is outside allowed range
      if (startDate.isBefore(actualMinStartDate, 'day') || startDate.isAfter(actualMaxStartDate, 'day')) {
        setError(
          t("packages.validation.startDateOutOfRange", {
            minDate: actualMinStartDate.format("DD/MM/YYYY"),
            maxDate: actualMaxStartDate.format("DD/MM/YYYY"),
          })
        );
        return;
      }

      // Check if end date is outside allowed range
      if (endDate.isBefore(actualMinEndDate, 'day') || endDate.isAfter(actualMaxEndDate, 'day')) {
        setError(
          t("packages.validation.endDateOutOfRange", {
            minDate: actualMinEndDate.format("DD/MM/YYYY"),
            maxDate: actualMaxEndDate.format("DD/MM/YYYY"),
          })
        );
        return;
      }

      // Check if start date is before end date
      if (startDate.isSameOrAfter(endDate)) {
        setError(t("packages.validation.invalidDateRange") || "Start date must be before end date");
        return;
      }
    } else {
      // Minimal validation: only check event date boundaries

      // Check if start date is before event start date
      if (startDate.isBefore(cityEventStartMoment, 'day')) {
        setError(
          t("packages.validation.startDateBeforeEvent", {
            eventStartDate: cityEventStartMoment.format("DD/MM/YYYY"),
          })
        );
        return;
      }

      // Check if end date is after event end date
      if (endDate.isAfter(cityEventEndMoment, 'day')) {
        setError(
          t("packages.validation.endDateAfterEvent", {
            eventEndDate: cityEventEndMoment.format("DD/MM/YYYY"),
          })
        );
        return;
      }

      // Check if start date is before end date
      if (startDate.isSameOrAfter(endDate)) {
        setError(t("packages.validation.invalidDateRange") || "Start date must be before end date");
        return;
      }
    }

    // All validations passed
    setError(undefined);
    onConfirm(startDate.format("YYYY-MM-DD"), endDate.format("YYYY-MM-DD"));
  };

  const modalContent = (
    <div className="space-y-6">
      <p className="text-neutral-600 dark:text-neutral-400">
        {t("packages.message.canCheckInEarlierOrCheckOutLater")}
      </p>

      {/* Date Range Pickers */}
      <div className="grid grid-cols-2 gap-4">
        {/* Start Date */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
            {t("hotelDetail.from") || "From"}
          </label>
          <div className="relative">
            <input
              type="text"
              readOnly
              onClick={() => setFocusedInputStart(true)}
              className="w-full px-4 py-2 rounded-lg border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-white cursor-pointer"
              value={startDate ? startDate.locale(languageCode).format("DD/MM/YYYY") : ""}
            />
            {focusedInputStart && (
              <div className="absolute top-12 left-0 z-50">
                <SingleDatePicker
                  date={startDate}
                  onDateChange={(newDate) => {
                    setStartDate(newDate);
                    setError(undefined);
                  }}
                  focused={focusedInputStart}
                  onFocusChange={(focus: any) => setFocusedInputStart(focus.focused)}
                  id={startDateId}
                  numberOfMonths={1}
                  daySize={40}
                  noBorder
                  hideKeyboardShortcutsPanel
                  isOutsideRange={(day) =>
                    isPackageDates
                      ? day.isBefore(actualMinStartDate, 'day') || day.isAfter(actualMaxStartDate, 'day')
                      : day.isBefore(cityEventStartMoment, 'day') || day.isAfter(cityEventEndMoment, 'day')
                  }
                />
              </div>
            )}
          </div>
        </div>

        {/* End Date */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
            {t("hotelDetail.to") || "To"}
          </label>
          <div className="relative">
            <input
              type="text"
              readOnly
              onClick={() => setFocusedInputEnd(true)}
              className="w-full px-4 py-2 rounded-lg border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-white cursor-pointer"
              value={endDate ? endDate.locale(languageCode).format("DD/MM/YYYY") : ""}
            />
            {focusedInputEnd && (
              <div className="absolute top-12 left-0 z-50">
                <SingleDatePicker
                  date={endDate}
                  onDateChange={(newDate) => {
                    setEndDate(newDate);
                    setError(undefined);
                  }}
                  focused={focusedInputEnd}
                  onFocusChange={(focus: any) => setFocusedInputEnd(focus.focused)}
                  id={endDateId}
                  numberOfMonths={1}
                  daySize={40}
                  noBorder
                  hideKeyboardShortcutsPanel
                  isOutsideRange={(day) =>
                    isPackageDates
                      ? day.isBefore(actualMinEndDate, 'day') || day.isAfter(actualMaxEndDate, 'day')
                      : day.isBefore(cityEventStartMoment, 'day') || day.isAfter(cityEventEndMoment, 'day')
                  }
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Date Range Display */}
      <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <p className="text-sm text-blue-900 dark:text-blue-100">
          {startDate && endDate && (
            <>
              {t("hotelDetail.from") || "From"}
              <strong>{startDate.locale(languageCode).format("DD/MM/YYYY")}</strong> {t("hotelDetail.to") || "to"}
              <strong>{endDate.locale(languageCode).format("DD/MM/YYYY")}</strong>
            </>
          )}
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-sm text-red-900 dark:text-red-100 flex items-center">
            <i className="las la-exclamation-circle mr-2"></i>
            {error}
          </p>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex justify-between pt-4">
        <ButtonSecondary onClick={onClose}>
          {t("common.actions.back") || "Back"}
        </ButtonSecondary>
        <ButtonPrimary onClick={handleConfirm}>
          {t("common.actions.confirm") || "Confirm"}
        </ButtonPrimary>
      </div>
    </div>
  );

  return (
    <NcModal
      contentExtraClass="max-w-screen-sm overflow-visible"
      modalTitle={t("packages.message.confirmAccommodationDates")}
      renderContent={() => modalContent}
      renderTrigger={() => null}
      isOpenProp={isOpen}
      onCloseModal={onClose}
    />
  );
};

export default HotelWizardRoomDateSelectorModal;

